/**
 * Right-pane detail for the selected Binom path. Hosts both pickers + edit
 * dialogs and pre-binds CRUD callbacks to the active path locator.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useState } from 'react';

import type {
  BinomDraftItem,
  BinomDraftPath,
} from '../../binom-flow-draft.types';
import BinomCampaignAsOfferPicker from '../pickers/BinomCampaignAsOfferPicker';
import BinomLanderPicker from '../pickers/BinomLanderPicker';
import BinomOfferPicker from '../pickers/BinomOfferPicker';
import BinomLanderEditDialog from '../dialogs/BinomLanderEditDialog';
import BinomOfferEditDialog from '../dialogs/BinomOfferEditDialog';

import BinomDirectLanderButton from './controls/BinomDirectLanderButton';
import BinomDirectUrlOfferDialog from './controls/BinomDirectUrlOfferDialog';
import BinomOfferExtraButtons from './controls/BinomOfferExtraButtons';
import BinomPathHeader from './controls/BinomPathHeader';
import BinomLandersOffersBlocks from './BinomLandersOffersBlocks';

export interface BinomPathDetailProps {
  path: BinomDraftPath;
  onPatch: (mutator: (p: BinomDraftPath) => void) => void;
  onRemove?: () => void;
  onAddLanders: (items: BinomDraftItem[]) => void;
  onRemoveLander: (idx: number) => void;
  onMoveLander: (from: number, to: number) => void;
  onAddOffers: (items: BinomDraftItem[]) => void;
  onRemoveOffer: (idx: number) => void;
  onMoveOffer: (from: number, to: number) => void;
}

const BinomPathDetail: FC<BinomPathDetailProps> = ({
  path,
  onPatch,
  onRemove,
  onAddLanders,
  onRemoveLander,
  onMoveLander,
  onAddOffers,
  onRemoveOffer,
  onMoveOffer,
}) => {
  const [landerPickerOpen, setLanderPickerOpen] = useState(false);
  const [offerPickerOpen, setOfferPickerOpen] = useState(false);
  const [directUrlOpen, setDirectUrlOpen] = useState(false);
  const [campaignOfferOpen, setCampaignOfferOpen] = useState(false);
  const [editLanderId, setEditLanderId] = useState<string | null>(null);
  const [editOfferId, setEditOfferId] = useState<string | null>(null);
  const attachOffer = (item: BinomDraftItem) => onAddOffers([item]);
  const attachLander = (item: BinomDraftItem) => onAddLanders([item]);

  const handleEditLander = (item: BinomDraftItem) => {
    if (item && !item.id.startsWith('new:')) setEditLanderId(item.id);
  };
  const handleEditOffer = (item: BinomDraftItem) => {
    if (
      item &&
      !item.id.startsWith('directUrl:') &&
      !item.id.startsWith('campaign:')
    ) {
      setEditOfferId(item.id);
    }
  };

  return (
    <div className="space-y-2.5">
      <BinomPathHeader path={path} onPatch={onPatch} onRemove={onRemove} />
      <BinomLandersOffersBlocks
        path={path}
        onPatch={onPatch}
        onAddLander={() => setLanderPickerOpen(true)}
        onRemoveLander={onRemoveLander}
        onMoveLander={onMoveLander}
        onEditLander={handleEditLander}
        onAddOffer={() => setOfferPickerOpen(true)}
        onRemoveOffer={onRemoveOffer}
        onMoveOffer={onMoveOffer}
        onEditOffer={handleEditOffer}
        landerExtra={<BinomDirectLanderButton onAttach={attachLander} />}
        offerExtra={
          <BinomOfferExtraButtons
            onDirectUrl={() => setDirectUrlOpen(true)}
            onCampaign={() => setCampaignOfferOpen(true)}
          />
        }
      />
      <BinomLanderPicker
        open={landerPickerOpen}
        onClose={() => setLanderPickerOpen(false)}
        excludeIds={path.landers.map((l) => l.id)}
        onAttach={(landings) =>
          onAddLanders(
            landings.map((l) => ({
              id: l.id,
              name: l.name,
              weight: 100,
              originalWeight: 100,
              active: true,
              originalActive: true,
            })),
          )
        }
      />
      <BinomOfferPicker
        open={offerPickerOpen}
        onClose={() => setOfferPickerOpen(false)}
        excludeIds={path.offers.map((o) => o.id)}
        onAttach={onAddOffers}
      />
      <BinomDirectUrlOfferDialog
        open={directUrlOpen}
        onClose={() => setDirectUrlOpen(false)}
        onAttach={attachOffer}
      />
      <BinomCampaignAsOfferPicker
        open={campaignOfferOpen}
        onClose={() => setCampaignOfferOpen(false)}
        excludeIds={path.offers.map((o) => o.id)}
        onAttach={attachOffer}
      />
      {editLanderId && (
        <BinomLanderEditDialog
          landerId={editLanderId}
          onClose={() => setEditLanderId(null)}
        />
      )}
      {editOfferId && (
        <BinomOfferEditDialog
          offerId={editOfferId}
          onClose={() => setEditOfferId(null)}
        />
      )}
    </div>
  );
};

export default BinomPathDetail;
